/**
 * DashboardPage — the app's primary surface (Phase 5).
 *
 * Renders one `KidCard` per child in the family's display order.
 * Empty state when the family has no children yet. Loading state
 * while `useChildren` is fetching.
 *
 * The dashboard is intentionally spare: the cards themselves carry
 * the visual weight. Only the eyebrow, hero greeting, and optional
 * family-summary pill live at the top.
 */

import { Link } from "react-router-dom";

import { LoadingShell } from "@/components/LoadingShell";
import { ActivityCard } from "@/features/activities/ActivityCard";
import {
  useFamilySuggestions,
  useRefreshFamilySuggestions,
} from "@/features/activities/useActivities";
import { useAuth } from "@/features/auth/useAuth";
import { useChildren } from "@/features/children/useChildren";
import { KidCard } from "@/features/perspectives/KidCard";

export function DashboardPage() {
  const { user, family } = useAuth();
  const { data: children, isLoading, error } = useChildren();
  const { data: suggestions } = useFamilySuggestions();
  const refresh = useRefreshFamilySuggestions();

  if (isLoading) {
    return <LoadingShell message="Loading dashboard…" />;
  }

  const greeting =
    user?.preferred_name ??
    (user?.display_name ?? "").split(" ")[0] ??
    "friend";

  return (
    <main className="max-w-[1040px] mx-auto px-6 md:px-11 pt-14 md:pt-16 pb-24">
      <div className="flex flex-col gap-2 mb-10">
        <div className="font-sans text-eyebrow uppercase text-coral-deep font-semibold">
          {family?.name ?? "Dashboard"}
        </div>
        <h1 className="font-serif text-display-md text-ink">
          Hey, <em className="italic font-normal text-coral-deep">{greeting}</em>
        </h1>
        <p className="max-w-[560px] font-sans text-[14px] italic text-ink-muted">
          Time with your kids is finite. Here's what's left.
        </p>
      </div>

      {error !== null && error !== undefined ? (
        <div
          role="alert"
          className="rounded-md border border-coral-deep/30 bg-coral-soft/40 px-5 py-4 font-sans text-[14px] text-coral-deep"
        >
          Couldn't load children. Refresh to try again.
        </div>
      ) : children !== undefined && children.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="flex flex-col gap-6">
          {(children ?? []).map((child) => (
            <KidCard key={child.id} child={child} />
          ))}
        </div>
      )}

      {suggestions !== undefined && suggestions.length === 0 && (
        <section className="mt-12">
          <div className="rounded-lg bg-surface border border-edge px-6 py-6 max-w-[560px] flex flex-col gap-3">
            <div className="font-serif text-[18px] text-ink">
              You've seen all the ideas for now.
            </div>
            <p className="font-sans text-[13px] text-ink-muted leading-[1.6]">
              Tap "New ideas" to cycle back through the catalog with a fresh shuffle,
              or check back next season for new seasonal picks.
            </p>
            <button
              type="button"
              onClick={() => refresh.mutate()}
              disabled={refresh.isPending}
              className="self-start px-4 py-2 rounded-md font-sans text-[13px] font-semibold text-white disabled:opacity-60"
              style={{
                background: "linear-gradient(135deg, var(--coral), var(--coral-deep))",
                boxShadow: "0 4px 12px rgba(251,118,84,0.35)",
              }}
            >
              {refresh.isPending ? "..." : "New ideas"}
            </button>
          </div>
        </section>
      )}

      {suggestions !== undefined && suggestions.length > 0 && (
        <section className="mt-12">
          <div className="flex flex-col gap-2 mb-6">
            <div className="flex items-center gap-3">
              <div className="font-sans text-eyebrow uppercase text-coral-deep font-semibold">
                {currentSeasonLabel()} picks
              </div>
              <span className="font-sans text-[10px] uppercase tracking-[0.14em] text-ink-dim bg-amber-soft/50 border border-amber/20 rounded-pill px-2 py-0.5">
                {currentSeasonEmoji()}
              </span>
            </div>
            <h2 className="font-serif text-[24px] md:text-[28px] text-ink">
              Make it <em className="italic font-normal text-coral-deep">count</em>
            </h2>
            <div className="flex items-center gap-4">
              <p className="font-sans text-[13px] text-ink-muted max-w-[520px]">
                Research-backed activities matched to your children's ages and
                the perspectives that matter most right now.
              </p>
              <button
                type="button"
                onClick={() => refresh.mutate()}
                disabled={refresh.isPending}
                className="shrink-0 px-3 py-1.5 rounded-md font-sans text-[12px] text-ink-muted border border-edge hover:border-coral-deep hover:text-coral-deep disabled:opacity-50"
              >
                {refresh.isPending ? "..." : "New ideas"}
              </button>
            </div>
          </div>
          <div className="flex flex-col gap-4">
            {suggestions.map((s) => (
              <ActivityCard key={s.id} suggestion={s} compact />
            ))}
          </div>
        </section>
      )}
    </main>
  );
}

// ---------------------------------------------------------------------------
// Empty state
// ---------------------------------------------------------------------------

function EmptyState() {
  return (
    <div className="rounded-lg bg-surface border border-edge shadow-sm px-8 py-12 flex flex-col gap-4 items-start max-w-[640px]">
      <div className="font-sans text-eyebrow uppercase text-coral-deep font-semibold">
        No children yet
      </div>
      <h2 className="font-serif text-[28px] text-ink leading-snug">
        Add your first child to see your{" "}
        <em className="italic font-normal text-coral-deep">Perspectives</em>.
      </h2>
      <p className="max-w-[520px] font-sans text-[14px] text-ink-muted leading-[1.6]">
        18 Summers, Santa Years, Family Dinners Until 18, and more —
        everything calculates automatically from their birthday. You can
        edit or remove children any time.
      </p>
      <Link
        to="/children"
        className="mt-2 inline-flex items-center px-6 py-3 rounded-md font-sans font-semibold text-white shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-amber"
        style={{
          background: "linear-gradient(135deg, var(--coral), var(--coral-deep))",
          boxShadow: "0 4px 12px rgba(251,118,84,0.35)",
        }}
      >
        Add a child
      </Link>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Season helpers
// ---------------------------------------------------------------------------

function currentSeasonLabel(): string {
  const month = new Date().getMonth() + 1;
  if (month >= 3 && month <= 5) return "Spring";
  if (month >= 6 && month <= 8) return "Summer";
  if (month >= 9 && month <= 11) return "Fall";
  return "Winter";
}

function currentSeasonEmoji(): string {
  const month = new Date().getMonth() + 1;
  if (month >= 3 && month <= 5) return "🌱";
  if (month >= 6 && month <= 8) return "☀️";
  if (month >= 9 && month <= 11) return "🍂";
  return "❄️";
}
